import React from 'react';

import { Box, Container, Group, Popover, SegmentedControl, Text, createStyles } from '@mantine/core';
import { DatePicker, DateValue } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { CalendarNav, CalendarPrev, CalendarToday, CalendarNext } from '@mobiscroll/react';
import dayjs from 'dayjs';

import { ProgramCalendarView } from './types';

const useStyles = createStyles(theme => ({
  headerWrapper: {
    width: '100%',
    fontFamily: `${theme.fontFamily} !important`,
    paddingTop: theme.spacing.xs,
    fontSize: theme.fontSizes.sm,
    borderBottom: 0,
  },
  fontOverride: {
    fontFamily: `${theme.fontFamily} !important`,
  },
  navTitle: {
    fontFamily: `${theme.fontFamily} !important`,
    '& .mbsc-calendar-title': {
      fontSize: `${theme.fontSizes.sm} !important`,
      fontWeight: 700,
      color: `${theme.colors.kiloBlue[6]} !important`,
    },
  },
}));

interface IProgramCalendarNavigationButtonsProps {
  view: ProgramCalendarView;
  setView: React.Dispatch<React.SetStateAction<ProgramCalendarView>>;
  selectedDate?: Date;
  onDateSelect?: (date: Date) => void;
}

const ProgramCalendarNavigationButtons = ({
  view,
  setView,
  selectedDate,
  onDateSelect,
}: IProgramCalendarNavigationButtonsProps): JSX.Element => {
  const { classes } = useStyles();
  const [datePickerOpened, { open: openDatePicker, close: closeDatePicker }] = useDisclosure(false);

  const handleDatePickerChange = (date: DateValue): void => {
    if (date && onDateSelect) {
      onDateSelect(date);
      closeDatePicker();
    }
  };

  const showDatePicker = view === 'agenda' && selectedDate && onDateSelect;

  return (
    <Container fluid className={classes.headerWrapper}>
      <Group grow noWrap spacing={0}>
        <Box />
        <Group position='center'>
          <SegmentedControl
            data-cy='programCalendarViewSwitcher'
            size='sm'
            value={view}
            onChange={(value: ProgramCalendarView) => setView(value)}
            data={[
              { label: 'Month', value: 'month' },
              { label: 'Week', value: 'week' },
              { label: 'Day', value: 'agenda' },
            ]}
          />
        </Group>
        <Group noWrap spacing={4} position='right'>
          <CalendarToday className={classes.fontOverride} />
          <CalendarPrev className={classes.fontOverride} />
          {showDatePicker ? (
            <Popover
              opened={datePickerOpened}
              onClose={closeDatePicker}
              closeOnEscape
              position='bottom'
              shadow='md'
              radius='md'
              withinPortal
            >
              <Popover.Target>
                <Text
                  className={classes.fontOverride}
                  size='sm'
                  fw={700}
                  color='kiloBlue.6'
                  onClick={openDatePicker}
                  sx={{ cursor: 'pointer' }}
                  data-cy='agendaDateLabel'
                >
                  {dayjs(selectedDate).format('dddd, MMMM D')}
                </Text>
              </Popover.Target>
              <Popover.Dropdown>
                <DatePicker value={selectedDate} onChange={handleDatePickerChange} />
              </Popover.Dropdown>
            </Popover>
          ) : (
            <CalendarNav className={classes.navTitle} />
          )}
          <CalendarNext className={classes.fontOverride} />
        </Group>
      </Group>
    </Container>
  );
};

export default ProgramCalendarNavigationButtons;
